import React, { createContext, useContext, useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import { API_URL } from '../utils/api';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [notifiedEvents, setNotifiedEvents] = useState([]);

  useEffect(() => {
    if ('Notification' in window) {
      Notification.requestPermission();
    }
    
    // Load notified events from localStorage to prevent duplicate notifications
    const savedNotifiedEvents = localStorage.getItem('notifiedEvents');
    if (savedNotifiedEvents) {
      setNotifiedEvents(JSON.parse(savedNotifiedEvents));
    }
  }, []);

  // Save notified events to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('notifiedEvents', JSON.stringify(notifiedEvents));
  }, [notifiedEvents]);

  const checkUpcomingEvents = async () => {
    try {
      // Try to get calendar events from API
      let calendarEvents = [];
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/events`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        calendarEvents = response.data;
        console.log('Calendar Events:', calendarEvents);
      } catch (apiError) {
        console.log('API Error loading calendar events:', apiError.message);
        // If API fails, use an empty array
      }

      // Process anniversary from localStorage
      const processAnniversary = () => {
        const anniversaryDate = localStorage.getItem('anniversaryDate');
        if (!anniversaryDate) return null;
        
        const originalDate = new Date(anniversaryDate);
        
        // Get the next occurrence of the anniversary (this year or next)
        const today = new Date();
        const currentYear = today.getFullYear();
        
        // Create this year's anniversary date
        const thisYearDate = new Date(originalDate);
        thisYearDate.setFullYear(currentYear);
        
        // Create next year's anniversary date
        const nextYearDate = new Date(originalDate);
        nextYearDate.setFullYear(currentYear + 1);
        
        // Determine which one is the next occurrence
        const nextAnniversary = thisYearDate > today ? thisYearDate : nextYearDate;
        
        return {
          id: `anniversary-${nextAnniversary.getTime()}`,
          title: 'Your Anniversary',
          date: nextAnniversary.toISOString(),
          type: 'anniversary',
          description: 'Celebrate your special day!',
          // Calculate which anniversary it is
          years: nextAnniversary.getFullYear() - originalDate.getFullYear()
        };
      };

      const anniversaryEvent = processAnniversary();
      if (anniversaryEvent) {
        console.log('Anniversary Event:', anniversaryEvent);
      }

      // Combine all events
      let allEvents = [...calendarEvents];
      if (anniversaryEvent) {
        allEvents.push(anniversaryEvent);
      }

      // Check for specific notification thresholds
      const now = new Date();
      
      allEvents.forEach(event => {
        const eventDate = new Date(event.date);
        const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
        
        console.log(`Event: ${event.title}, Date: ${eventDate}, Days until: ${daysUntil}`);
        
        // Check if we've already notified for this event at this threshold
        const eventKey = `${event.id}-${daysUntil}`;
        if (notifiedEvents.includes(eventKey)) {
          return;
        }
        
        // Specific thresholds for different event types
        let shouldNotify = false;
        let message = '';
        
        if (event.type === 'anniversary' && (daysUntil === 7 || daysUntil === 3 || daysUntil === 1 || daysUntil === 0)) {
          shouldNotify = true;
          const yearText = event.years > 0 ? ` (${event.years} year${event.years > 1 ? 's' : ''})` : '';
          
          if (daysUntil === 0) {
            message = `Today is your Anniversary${yearText}! 🎉`;
          } else if (daysUntil === 1) {
            message = `Your Anniversary${yearText} is tomorrow!`;
          } else {
            message = `Your Anniversary${yearText} is coming up in ${daysUntil} days!`;
          }
        } else if (daysUntil === 3 || daysUntil === 1 || daysUntil === 0) {
          shouldNotify = true;
          
          if (daysUntil === 0) {
            message = `Today is ${event.title}! 🎉`;
          } else if (daysUntil === 1) {
            message = `${event.title} is tomorrow!`;
          } else {
            message = `${event.title} is coming up in ${daysUntil} days!`;
          }
        }
        
        if (shouldNotify && daysUntil >= 0) {
          console.log(`Sending notification for: ${event.title} (${daysUntil} days)`);
          
          // Add to in-app notifications
          setNotifications(prev => [...prev, {
            id: event.id,
            title: event.title,
            message: message,
            date: event.date,
            type: event.type,
            read: false
          }]);

          // Show push notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(event.type === 'anniversary' ? 'Anniversary Reminder' : 'Event Reminder', {
              body: message,
              icon: '/favicon.ico'
            });
          }

          // Show snackbar
          setSnackbar({
            open: true,
            message: message,
            severity: 'info'
          });
          
          // Record that we've notified for this event at this threshold
          setNotifiedEvents(prev => [...prev, eventKey]);
        }
      });
    } catch (error) {
      console.error('Error checking upcoming events:', error);
    }
  };

  useEffect(() => {
    // Check for upcoming events on mount, every minute, and on window focus
    checkUpcomingEvents();
    const interval = setInterval(checkUpcomingEvents, 60 * 1000); // Every minute
    const onFocus = () => checkUpcomingEvents();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const clearNotification = (notificationId) => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== notificationId)
    );
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Add a generic notify function
  const notify = ({ id, title, message, type = 'info', date = new Date().toISOString() }) => {
    setNotifications(prev => [
      ...prev,
      {
        id: id || `${type}-${Date.now()}`,
        title,
        message,
        date,
        type,
        read: false
      }
    ]);
    if (Notification.permission === 'granted') {
      new Notification(title, {
        body: message,
        icon: '/favicon.ico'
      });
    }
    setSnackbar({
      open: true,
      message,
      severity: 'info'
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        markAsRead,
        clearNotification,
        setSnackbar,
        notify // Expose notify function
      }}
    >
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}; 